import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth, api } from '../App';
import { ASSETS } from '../constants/site';

export default function VerifyEmail() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ phase: 'loading', message: '' });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState({ phase: 'error', message: 'This activation link is missing its token. Please request a new one.' });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post('/auth/verify-email', { token });
        const me = await api.get('/auth/me').then((r) => r.data).catch(() => data);
        setUser(me);
        setState({ phase: 'done', message: '' });
        setTimeout(() => navigate('/account', { state: { user: me } }), 1500);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setState({ phase: 'error', message: typeof detail === 'string' ? detail : 'Activation failed.' });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050B14] circuit-bg flex items-center justify-center px-6" data-testid="verify-email-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="gold-ring rounded-3xl bg-[#0F1C33]/80 p-10 max-w-md w-full text-center">
        <img src={ASSETS.icon} alt="GSLAB" className="h-14 w-14 rounded-xl mx-auto mb-6" />
        {state.phase === 'loading' && (
          <>
            <Loader2 size={26} className="text-amber-400 animate-spin mx-auto mb-4" />
            <h1 className="font-display font-bold text-xl">Activating your account…</h1>
          </>
        )}
        {state.phase === 'done' && (
          <>
            <CheckCircle2 size={30} className="text-emerald-300 mx-auto mb-4" />
            <h1 className="font-display font-bold text-xl mb-2" data-testid="verify-success-msg">Account activated</h1>
            <p className="text-sm text-slate-400">You're signed in — taking you to your portal.</p>
          </>
        )}
        {state.phase === 'error' && (
          <>
            <XCircle size={30} className="text-red-400 mx-auto mb-4" />
            <h1 className="font-display font-bold text-xl mb-2" data-testid="verify-error-msg">Activation failed</h1>
            <p className="text-sm text-slate-400 mb-6">{state.message}</p>
            <Link to="/login" data-testid="verify-back-login-link"
              className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-8 py-3 text-sm">
              Back to sign in
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
